import type { TimelineCustomRange, ZoomLevel } from "./timeline-range.utils";
import { DEFAULT_ZOOM_LEVEL } from "./timeline-range.utils";

const ZOOM_LEVELS: ZoomLevel[] = [
  "6months",
  "18months",
  "1year",
  "3years",
  "5years",
  "10years",
  "custom",
];

export interface StoredTimelineRangePreference {
  zoomLevel: ZoomLevel;
  customRange?: {
    startDate: string;
    endDate: string;
  } | null;
  zoomCenter?: string | null;
}

export interface TimelineRangePreference {
  zoomLevel: ZoomLevel;
  customDateRange: TimelineCustomRange | null;
  zoomCenter: Date | null;
}

export function isZoomLevel(value: unknown): value is ZoomLevel {
  return typeof value === "string" && ZOOM_LEVELS.includes(value as ZoomLevel);
}

function startupStorageKey(userId: string, startupId: string): string {
  return `timelineRange_${userId}_${startupId}`;
}

function globalStorageKey(userId: string): string {
  return `timelineRange_${userId}_last`;
}

function parseStoredPreference(
  raw: string | null,
): StoredTimelineRangePreference | null {
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw) as StoredTimelineRangePreference;
    if (!isZoomLevel(parsed.zoomLevel)) return null;

    if (parsed.zoomLevel === "custom") {
      const start = parsed.customRange?.startDate;
      const end = parsed.customRange?.endDate;
      if (!start || !end) return null;

      const startDate = new Date(start);
      const endDate = new Date(end);
      if (
        Number.isNaN(startDate.getTime()) ||
        Number.isNaN(endDate.getTime()) ||
        startDate.getTime() >= endDate.getTime()
      ) {
        return null;
      }
    }

    if (parsed.zoomCenter) {
      const center = new Date(parsed.zoomCenter);
      if (Number.isNaN(center.getTime())) {
        parsed.zoomCenter = null;
      }
    }

    return parsed;
  } catch {
    return null;
  }
}

function toTimelineRangePreference(
  stored: StoredTimelineRangePreference,
): TimelineRangePreference {
  return {
    zoomLevel: stored.zoomLevel,
    customDateRange:
      stored.zoomLevel === "custom" && stored.customRange
        ? {
            startDate: new Date(stored.customRange.startDate),
            endDate: new Date(stored.customRange.endDate),
          }
        : null,
    zoomCenter:
      stored.zoomLevel !== "custom" && stored.zoomCenter
        ? new Date(stored.zoomCenter)
        : null,
  };
}

export function loadTimelineRangePreference(
  userId: string,
  startupId: string,
): TimelineRangePreference | null {
  if (typeof window === "undefined") return null;

  const startupPref = parseStoredPreference(
    localStorage.getItem(startupStorageKey(userId, startupId)),
  );
  if (startupPref) {
    return toTimelineRangePreference(startupPref);
  }

  const globalPref = parseStoredPreference(
    localStorage.getItem(globalStorageKey(userId)),
  );
  if (globalPref) {
    return toTimelineRangePreference(globalPref);
  }

  return null;
}

export function saveTimelineRangePreference(
  userId: string,
  startupId: string,
  preference: TimelineRangePreference,
): void {
  if (typeof window === "undefined") return;

  const stored: StoredTimelineRangePreference = {
    zoomLevel: preference.zoomLevel,
    customRange:
      preference.zoomLevel === "custom" && preference.customDateRange
        ? {
            startDate: preference.customDateRange.startDate.toISOString(),
            endDate: preference.customDateRange.endDate.toISOString(),
          }
        : null,
    zoomCenter:
      preference.zoomLevel !== "custom" && preference.zoomCenter
        ? preference.zoomCenter.toISOString()
        : null,
  };

  const serialized = JSON.stringify(stored);
  localStorage.setItem(startupStorageKey(userId, startupId), serialized);
  localStorage.setItem(globalStorageKey(userId), serialized);
}

export function defaultTimelineRangePreference(): TimelineRangePreference {
  return {
    zoomLevel: DEFAULT_ZOOM_LEVEL,
    customDateRange: null,
    zoomCenter: null,
  };
}
